import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, Profile } from '../../lib/supabase';
import { checkExpiringDocuments, ExpiringDocument } from '../../lib/expirationTracker';
import { AlertTriangle, CheckCircle2, Download, TrendingUp, Shield, FileText, Users, XCircle } from 'lucide-react';

interface ContractorCompliance {
  contractor: Profile;
  certifications_count: number;
  insurance_count: number;
  expiring_count: number;
  expired_count: number;
  is_compliant: boolean;
}

export const ComplianceCenter: React.FC = () => {
  const { profile } = useAuth();
  const [complianceData, setComplianceData] = useState({
    verifiedPercentage: 0,
    expiringDocs: 0,
    totalContractors: 0,
    fullyCompliant: 0,
  });
  const [contractors, setContractors] = useState<ContractorCompliance[]>([]);
  const [alerts, setAlerts] = useState<ExpiringDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [savedContractorIds, setSavedContractorIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (profile) {
      loadComplianceData();
    }
  }, [profile]);

  const loadComplianceData = async () => {
    if (!profile) return;

    try {
      const { data: savedTrades } = await supabase
        .from('saved_trades')
        .select('contractor_id')
        .eq('gc_id', profile.id);

      const contractorIds = savedTrades?.map(st => st.contractor_id) || [];
      setSavedContractorIds(new Set(contractorIds));

      if (contractorIds.length === 0) {
        setLoading(false);
        return;
      }

      const { data: contractorProfiles } = await supabase
        .from('profiles')
        .select('*')
        .in('id', contractorIds);

      if (!contractorProfiles) {
        setLoading(false);
        return;
      }

      const contractorComplianceData = await Promise.all(
        contractorProfiles.map(async (contractor) => {
          const [certsRes, insuranceRes] = await Promise.all([
            supabase
              .from('certifications')
              .select('id, expiry_date, verified')
              .eq('user_id', contractor.id),
            supabase
              .from('insurance_policies')
              .select('id, expiry_date, status')
              .eq('user_id', contractor.id)
          ]);

          const certs = certsRes.data || [];
          const insurance = insuranceRes.data || [];
          const now = new Date();

          const expiring = [
            ...certs.filter(c => {
              if (!c.expiry_date) return false;
              const days = Math.ceil((new Date(c.expiry_date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
              return days > 0 && days <= 30;
            }),
            ...insurance.filter(i => i.status === 'expiring')
          ];

          const expired = [
            ...certs.filter(c => {
              if (!c.expiry_date) return false;
              const days = Math.ceil((new Date(c.expiry_date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
              return days < 0;
            }),
            ...insurance.filter(i => i.status === 'expired')
          ];

          const isCompliant =
            contractor.verification_status === 'verified' &&
            insurance.some(i => i.status === 'active') &&
            certs.some(c => c.verified) &&
            expired.length === 0;

          return {
            contractor,
            certifications_count: certs.filter(c => c.verified).length,
            insurance_count: insurance.filter(i => i.status === 'active').length,
            expiring_count: expiring.length,
            expired_count: expired.length,
            is_compliant: isCompliant
          };
        })
      );

      const verified = contractorComplianceData.filter(c => c.contractor.verification_status === 'verified').length;
      const fullyCompliant = contractorComplianceData.filter(c => c.is_compliant).length;
      const totalExpiring = contractorComplianceData.reduce((sum, c) => sum + c.expiring_count + c.expired_count, 0);

      setComplianceData({
        verifiedPercentage: contractorProfiles.length > 0 ? Math.round((verified / contractorProfiles.length) * 100) : 0,
        expiringDocs: totalExpiring,
        totalContractors: contractorProfiles.length,
        fullyCompliant
      });

      setContractors(contractorComplianceData);

      const allAlerts: ExpiringDocument[] = [];
      for (const cc of contractorComplianceData) {
        const docs = await checkExpiringDocuments(cc.contractor.id);
        allAlerts.push(...docs.filter(d => d.days_remaining <= 7));
      }
      setAlerts(allAlerts.slice(0, 10));

    } catch (error) {
      console.error('Error loading compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    const csvContent = [
      ['Contractor', 'Verified', 'Certifications', 'Active Insurance', 'Expiring/Expired', 'Compliant'].join(','),
      ...contractors.map(c => [
        c.contractor.full_name,
        c.contractor.verification_status === 'verified' ? 'Yes' : 'No',
        c.certifications_count,
        c.insurance_count,
        c.expiring_count + c.expired_count,
        c.is_compliant ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (contractors.length === 0) {
    return (
      <div className="space-y-6">
        <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900 rounded-2xl p-8 overflow-hidden">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="relative">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Compliance Center</h1>
              <p className="text-slate-300">Monitor contractor compliance and documentation</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Users className="w-10 h-10 text-white" />
          </div>
          <h3 className="font-bold text-slate-900 mb-2">No contractors in your roster</h3>
          <p className="text-slate-600">
            Add contractors to your roster to monitor their compliance status
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900 rounded-2xl p-8 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="relative">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Compliance Center</h1>
              <p className="text-slate-300">Monitor contractor compliance and documentation</p>
            </div>
            <button
              onClick={exportReport}
              className="flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-all shadow-lg hover:shadow-xl"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-white to-green-50 rounded-xl border border-green-200 p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-md">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">{complianceData.verifiedPercentage}%</div>
          <div className="text-sm text-slate-600 font-medium">Verified</div>
          <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full" style={{width: `${complianceData.verifiedPercentage}%`}}></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-amber-50 rounded-xl border border-amber-200 p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">{complianceData.expiringDocs}</div>
          <div className="text-sm text-slate-600 font-medium">Expiring/Expired</div>
          <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-500 to-orange-600 rounded-full" style={{width: '45%'}}></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-amber-50 rounded-xl border border-amber-200 p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-md">
              <Shield className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">{complianceData.fullyCompliant}</div>
          <div className="text-sm text-slate-600 font-medium">Fully Compliant</div>
          <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full" style={{width: `${complianceData.totalContractors > 0 ? (complianceData.fullyCompliant / complianceData.totalContractors) * 100 : 0}%`}}></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center shadow-md">
              <FileText className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">{complianceData.totalContractors}</div>
          <div className="text-sm text-slate-600 font-medium">Total Contractors</div>
          <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-slate-600 to-slate-700 rounded-full" style={{width: '100%'}}></div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
        <h2 className="text-xl font-bold text-slate-900 mb-1 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-amber-600" />
          Compliance Health
        </h2>
        <p className="text-sm text-slate-600 mb-6">Overall contractor compliance status</p>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Verified Contractors</span>
              <span className="text-sm font-semibold text-slate-900">{complianceData.verifiedPercentage}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all"
                style={{ width: `${complianceData.verifiedPercentage}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Fully Compliant</span>
              <span className="text-sm font-semibold text-slate-900">
                {complianceData.totalContractors > 0
                  ? Math.round((complianceData.fullyCompliant / complianceData.totalContractors) * 100)
                  : 0}%
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full transition-all"
                style={{
                  width: `${complianceData.totalContractors > 0
                    ? (complianceData.fullyCompliant / complianceData.totalContractors) * 100
                    : 0}%`
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Compliance Alerts</h2>

          <div className="space-y-3">
            {alerts.map((alert, index) => (
              <div
                key={`${alert.id}-${index}`}
                className={`flex items-start justify-between p-4 rounded-lg border-2 ${
                  alert.days_remaining < 0
                    ? 'bg-red-50 border-red-200'
                    : 'bg-amber-50 border-amber-200'
                }`}
              >
                <div className="flex items-start space-x-3 flex-1">
                  {alert.days_remaining < 0 ? (
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h3 className={`font-semibold ${
                      alert.days_remaining < 0 ? 'text-red-900' : 'text-amber-900'
                    }`}>
                      {alert.name}
                    </h3>
                    <p className={`text-sm mt-1 ${
                      alert.days_remaining < 0 ? 'text-red-700' : 'text-amber-700'
                    }`}>
                      {alert.days_remaining < 0
                        ? `Expired ${Math.abs(alert.days_remaining)} days ago`
                        : `Expires in ${alert.days_remaining} days`}
                    </p>
                    <p className={`text-xs mt-1 ${
                      alert.days_remaining < 0 ? 'text-red-600' : 'text-amber-600'
                    }`}>
                      {new Date(alert.expiry_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
        <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2 text-amber-600" />
          Contractor Status
        </h2>

        <div className="space-y-3">
          {contractors.map((cc) => (
            <div key={cc.contractor.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-amber-50 hover:shadow-md transition-all">
              <div className="flex items-center space-x-4 flex-1">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                  <span className="text-sm font-semibold text-slate-700">
                    {cc.contractor.full_name?.charAt(0) || '?'}
                  </span>
                </div>

                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">{cc.contractor.full_name}</h3>
                  <div className="flex items-center space-x-3 text-xs text-slate-600 mt-1">
                    <span>{cc.certifications_count} certs</span>
                    <span>•</span>
                    <span>{cc.insurance_count} policies</span>
                    {(cc.expiring_count + cc.expired_count) > 0 && (
                      <>
                        <span>•</span>
                        <span className="text-amber-600 font-medium">
                          {cc.expiring_count + cc.expired_count} expiring/expired
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {cc.is_compliant ? (
                <div className="flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Compliant
                </div>
              ) : (
                <div className="flex items-center px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Review Needed
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
